import { z } from 'zod';
import { ACTION_NAME_PATTERN } from '../agent-studio/name-validation';
import { actionParameterSchema } from './agent-draft/fields';
import { replaceTextOnce, TextEditError } from './agent-draft/replace-text';
import { workflowStepsSchema } from './agent-draft/workflow-steps';
import { agentCheckIssueSchema, textEditSchema } from './agent-tool-schemas';
// Email agents, their playbooks and their actions, read through the endpoints their pages use. Those endpoints are
// open only to members on the strada-admin flag (their 403 carries STRADA_ADMIN_REQUIRED_CODE), and every save on them
// is live: an email agent has no draft.
export const EMAIL_AGENTS_OFF_MESSAGE = "Email agents aren't turned on for your account. Nothing changed.";
export const EMAIL_AGENTS_UNCHECKED_MESSAGE = "Couldn't check whether email agents are on for you. Try again.";
/** Why a save was refused before it landed, in the words its step shows after "Not saved: ". */
export const SAVE_REFUSALS = {
    changedSinceRead: 'changed since Atlas read it',
    openWithUnsavedChanges: 'open with unsaved changes'
};
/** Each Reply mode in its channel page's words. */
export const EMAIL_CHANNEL_REPLY_MODES = {
    off: 'Agent off — AI does not process emails',
    'replies-disabled': 'Replies disabled — AI acts but does not reply',
    'auto-send': 'Replies enabled — AI replies automatically'
};
export const HUBSPOT_CHANNEL_REPLY_MODES = {
    off: 'Agent off — store messages only',
    draft: 'Replies drafted — AI drafts for support to review',
    'auto-send': 'Replies enabled — AI replies automatically'
};
export const emailChannelReplyMode = ({ autoReplyEnabledAt, repliesDisabledAt }) => {
    if (!autoReplyEnabledAt)
        return 'off';
    return repliesDisabledAt ? 'replies-disabled' : 'auto-send';
};
export const hubspotChannelReplyMode = ({ autoReplyEnabled, draftModeEnabled }) => {
    if (!autoReplyEnabled)
        return 'off';
    return draftModeEnabled ? 'draft' : 'auto-send';
};
/** The channels that send an email agent its mail, each with its Reply mode. */
export const describeEmailAgentChannels = (agentId, emailChannels, hubspotChannels) => [
    ...emailChannels
        .filter((channel) => channel.agentId === agentId)
        .map((channel) => ({
        channel: channel.emailAddress,
        replyMode: EMAIL_CHANNEL_REPLY_MODES[emailChannelReplyMode(channel)]
    })),
    ...hubspotChannels
        .filter((channel) => channel.agentId === agentId)
        .map((channel) => ({
        channel: 'HubSpot',
        replyMode: HUBSPOT_CHANNEL_REPLY_MODES[hubspotChannelReplyMode(channel)]
    }))
];
const savedAtSchema = z.string().describe('When it was last saved; every save is live straight away');
const unsavedChangesSchema = z
    .boolean()
    .optional()
    .describe("True when the user has it open with changes they haven't saved. Its page's Save sends every field, so before you propose a change ask them to save it, or to leave it with Leave without saving, then read it again");
const triggerConditionSchema = z
    .string()
    .describe('When an email agent picks this playbook for an email: its "Trigger when"');
const inputsSchema = z
    .record(z.string(), actionParameterSchema)
    .describe("The action's inputs, each described for the email agent that fills it");
const inputSettingSchema = z.object({
    input: z.string().describe('A dot path for a nested field, such as address.city'),
    setting: z.enum(['Fixed', 'Override']),
    value: z.unknown().optional().describe('Fixed: the value it always gets'),
    instructions: z.string().optional().describe('Override: how this agent fills it'),
    allowedValues: z.array(z.string()).optional()
});
export const listEmailAgentsToolSchema = {
    name: 'list_email_agents',
    description: "List the organization's email agents: agents that read and answer email threads, which list_agents doesn't show. available is false when email agents aren't turned on for the user; then don't bring them up unless the user asks about them.",
    inputSchema: z.object({}),
    outputSchema: z.object({
        available: z.boolean(),
        emailAgents: z.array(z.object({ emailAgentId: z.string(), name: z.string(), savedAt: savedAtSchema }))
    })
};
export const getEmailAgentToolSchema = {
    name: 'get_email_agent',
    description: 'Read an email agent as last saved, which is what the next email gets: its Persona and Objective, the playbooks it links with their trigger conditions, its actions with their inputs, and the channels that send it mail with their Reply mode. An action that lists playbooks runs only while one of them is in use.',
    inputSchema: z.object({ emailAgentId: z.string().describe('From list_email_agents') }),
    outputSchema: z.object({
        emailAgentId: z.string(),
        name: z.string(),
        versionId: z.string(),
        savedAt: savedAtSchema,
        persona: z.string(),
        objective: z.string(),
        playbooks: z
            .array(z.object({ playbookId: z.string(), name: z.string(), triggerCondition: triggerConditionSchema }))
            .describe('With none, the agent answers no email'),
        actions: z.array(z.object({
            actionId: z.string(),
            name: z.string(),
            inputs: inputsSchema,
            inputSettings: z
                .array(inputSettingSchema)
                .describe("This agent's inputs set to Fixed or Override. Every other input is Default: the agent fills it from the email, by the input's description"),
            playbooks: z
                .array(z.string())
                .optional()
                .describe('The linked playbooks that name it; it runs only while one of them is in use, otherwise on every email')
        })),
        channels: z
            .array(z.object({ channel: z.string(), replyMode: z.string() }))
            .describe('With none, the agent gets no mail'),
        unsavedChanges: unsavedChangesSchema,
        admin: z
            .object({
            decisionInstructions: z.string(),
            responseInstructions: z.string().optional(),
            model: z.string()
        })
            .optional()
            .describe('Only for Strada staff, who are the only ones who see or change these')
    })
};
export const listPlaybooksToolSchema = {
    name: 'list_playbooks',
    description: "List the organization's email playbooks with their trigger conditions. A playbook does nothing until an email agent links it.",
    inputSchema: z.object({}),
    outputSchema: z.object({
        playbooks: z.array(z.object({
            playbookId: z.string(),
            name: z.string(),
            triggerCondition: triggerConditionSchema,
            savedAt: savedAtSchema
        }))
    })
};
export const getPlaybookToolSchema = {
    name: 'get_playbook',
    description: 'Read an email playbook as last saved: its trigger condition, its instructions, and the email agents that link it. In the instructions, a name in backticks is an action.',
    inputSchema: z.object({ playbookId: z.string().describe('From list_playbooks or get_email_agent') }),
    outputSchema: z.object({
        playbookId: z.string(),
        name: z.string(),
        triggerCondition: triggerConditionSchema,
        instructions: z.string(),
        versionId: z.string(),
        savedAt: savedAtSchema,
        linkedAgents: z
            .array(z.object({ emailAgentId: z.string(), name: z.string() }))
            .describe('The email agents whose current version links it; a save reaches each of them'),
        unsavedChanges: unsavedChangesSchema
    })
};
export const listEmailActionsToolSchema = {
    name: 'list_email_actions',
    description: 'List the actions email agents can run: workflows shared across the organization, with their inputs and how many email agents and playbooks use each. A change to one changes every agent that uses it.',
    inputSchema: z.object({}),
    outputSchema: z.object({
        actions: z.array(z.object({
            actionId: z.string(),
            name: z.string(),
            inputs: inputsSchema,
            usedByAgents: z.number().describe('Email agents whose current version has it'),
            usedByPlaybooks: z.number().describe('Playbooks whose current version names it')
        }))
    })
};
export const getEmailActionToolSchema = {
    name: 'get_email_action',
    description: 'Read one email action: its inputs, whether its workflow is published, the published steps the agent runs, and whether edits wait unpublished in the builder.',
    inputSchema: z.object({ actionId: z.string().describe('From list_email_actions or get_email_agent') }),
    outputSchema: z.object({
        actionId: z.string(),
        name: z.string(),
        inputs: inputsSchema,
        published: z
            .boolean()
            .describe('Never published: an agent that runs it gets a failed result instead of an answer'),
        steps: z.array(z.record(z.string(), z.unknown())).describe('The published workflow, which is what runs'),
        unpublishedEdits: z
            .boolean()
            .describe('Someone saved changes in the builder that wait for Publish workflow; they are not in steps'),
        versionId: z.string().describe('Its latest saved version; update_email_action takes it'),
        usedByAgents: z
            .array(z.object({ emailAgentId: z.string(), name: z.string() }))
            .describe('Email agents that run it, directly or through a playbook they link that names it')
    })
};
const versionIdSchema = z
    .string()
    .describe('The versionId you read it at. If anyone saved it since, nothing is saved and you read it again');
const personaSchema = z
    .string()
    .describe("The Persona: who the agent is and how it writes, in the agent's own words");
const objectiveSchema = z
    .string()
    .describe('The Objective: what the agent is for, first in its instructions');
const instructionsSchema = z
    .string()
    .describe('What the agent does once it picks this playbook, as numbered steps. An action goes in backticks by its exact name, such as `lookup_policy_by_email`; nothing else goes in backticks');
const fixedInputsSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean()]));
const attachedActionSchema = z.object({
    actionId: z.string().describe('From list_email_actions'),
    fixedInputs: fixedInputsSchema
        .optional()
        .describe('Inputs that always get the value given here (Fixed), by name, with a dot path for a nested field. Every other input is Default: the agent fills it from the email, by its description')
});
const inputChangeSchema = z.object({
    actionId: z.string().describe('An action the agent has'),
    fixedInputs: fixedInputsSchema
        .optional()
        .describe('Inputs to set to Fixed, with the value each always gets'),
    defaultInputs: z.array(z.string()).optional().describe('Fixed inputs to set back to Default')
});
const savedSchema = z.object({ versionId: z.string(), savedAt: savedAtSchema });
export const createEmailAgentToolSchema = {
    name: 'create_email_agent',
    description: "Create an email agent in one save, with its playbooks and existing actions if given, and open its page. It answers no mail until the user connects a channel to it on the Email page, which Atlas doesn't do.",
    inputSchema: z.object({
        name: z.string().min(1),
        persona: personaSchema,
        objective: objectiveSchema,
        playbookIds: z
            .array(z.string())
            .optional()
            .describe('The playbooks it links, from list_playbooks. Each brings the actions it names'),
        actions: z.array(attachedActionSchema).optional()
    }),
    outputSchema: z.object({ emailAgentId: z.string(), name: z.string(), versionId: z.string() })
};
export const updateEmailAgentToolSchema = {
    name: 'update_email_agent',
    description: 'Save a change to an email agent. It is live for the next email as soon as it saves: email agents have no draft. Send only what changes.',
    inputSchema: z.object({
        emailAgentId: z.string(),
        versionId: versionIdSchema,
        persona: personaSchema.optional().describe('The whole new Persona'),
        objective: objectiveSchema.optional().describe('The whole new Objective'),
        edits: z
            .array(textEditSchema.extend({ field: z.enum(['persona', 'objective']) }))
            .optional()
            .describe('Changes to part of the Persona or Objective, applied in order. Use these for a small change'),
        linkPlaybooks: z
            .array(z.string())
            .optional()
            .describe("Playbook ids to link, from list_playbooks. Each attaches the actions it names that the agent doesn't have, with Default inputs"),
        unlinkPlaybooks: z.array(z.string()).optional().describe('Playbook ids to unlink'),
        attachActions: z.array(attachedActionSchema).optional().describe("Actions it doesn't have yet"),
        detachActions: z.array(z.string()).optional().describe('Action ids to detach'),
        changeActionInputs: z
            .array(inputChangeSchema)
            .optional()
            .describe("Inputs to change on actions it has. Every input not named keeps its setting; Override is set on the agent's page")
    }),
    outputSchema: savedSchema.extend({ emailAgentId: z.string() })
};
/**
 * The actions linking playbooks attaches, as the agent page's Add does: each one the playbooks name, once, unless the
 * same save already gives the agent that action or detaches it.
 */
export const actionsBroughtByLinks = (named, { has, detached = [] }) => [...new Set(named)].filter((actionId) => !has.includes(actionId) && !detached.includes(actionId));
/** Saved text after a write's whole replacement or its edits, applied in order. Throws when an edit can't apply. */
export const editedText = (saved, replacement, edits, where) => {
    if (replacement !== undefined && edits.length > 0) {
        throw new TextEditError(`This change both replaces and edits ${where}. Do one or the other.`, {
            failure: 'replaced-and-edited',
            where
        });
    }
    return edits.reduce((text, edit) => replaceTextOnce(text, edit, where), replacement ?? saved);
};
/** An email agent's Persona and Objective after update_email_agent's change to them. */
export const editedEmailAgentText = (saved, { persona, objective, edits = [] }) => ({
    persona: editedText(saved.persona, persona, edits.filter(({ field }) => field === 'persona'), 'the Persona'),
    objective: editedText(saved.objective, objective, edits.filter(({ field }) => field === 'objective'), 'the Objective')
});
export const createPlaybookToolSchema = {
    name: 'create_playbook',
    description: 'Create an email playbook. It is linked to nothing, so it does nothing until an email agent links it (update_email_agent or create_email_agent). Build the actions it names first.',
    inputSchema: z.object({
        name: z.string().min(1),
        triggerCondition: triggerConditionSchema,
        instructions: instructionsSchema
    }),
    outputSchema: z.object({ playbookId: z.string(), name: z.string() })
};
export const updatePlaybookToolSchema = {
    name: 'update_playbook',
    description: 'Save a change to an email playbook. Every email agent that links it uses the change for its next email: playbooks have no draft. Send only what changes.',
    inputSchema: z.object({
        playbookId: z.string(),
        versionId: versionIdSchema,
        name: z.string().min(1).optional(),
        triggerCondition: triggerConditionSchema.optional(),
        instructions: instructionsSchema.optional().describe('The whole new instructions'),
        edits: z
            .array(textEditSchema)
            .optional()
            .describe('Changes to part of the instructions, applied in order. Use these for a small change')
    }),
    outputSchema: savedSchema.extend({ playbookId: z.string() })
};
const actionInputsSchema = z
    .record(z.string(), actionParameterSchema)
    .describe("What the email agent fills from the email when it runs the action, available to steps as {{action.name}}. The agent reads only these descriptions and the action's name, so say in each what to put there");
const actionStepsSchema = workflowStepsSchema.describe('The steps that run, in order, ending each path with Return Response. Call list_workflow_nodes and get_workflow_node with workflow emailAction first');
const actionWarningsSchema = z
    .array(agentCheckIssueSchema)
    .describe("The check's warnings; errors stop the call before anything is saved");
export const createEmailActionToolSchema = {
    name: 'create_email_action',
    description: 'Build an action email agents can run: a workflow that reaches another system to fetch data or act. It is created, saved and published in one step, all or nothing, and no agent runs it until one has it or links a playbook that names it.',
    inputSchema: z.object({
        name: z
            .string()
            .regex(ACTION_NAME_PATTERN)
            .describe('snake_case, such as lookup_policy_by_email: unique in the organization, and what playbooks name'),
        inputs: actionInputsSchema,
        steps: actionStepsSchema
    }),
    outputSchema: z.object({ actionId: z.string(), name: z.string(), warnings: actionWarningsSchema })
};
export const updateEmailActionToolSchema = {
    name: 'update_email_action',
    description: 'Change and publish the inputs or steps of an action no email agent but the named one uses. It is live as soon as it saves. Refused when another agent uses it (build a new action and attach that instead), and while edits wait unpublished in the builder. Read it with get_email_action first.',
    inputSchema: z.object({
        actionId: z.string(),
        emailAgentId: z
            .string()
            .optional()
            .describe('The agent you change it for. Leave it out when no agent uses it'),
        versionId: versionIdSchema,
        inputs: actionInputsSchema.optional().describe('All of its inputs, as they should be'),
        steps: actionStepsSchema.optional().describe('All of its steps, as they should be')
    }),
    outputSchema: z.object({ actionId: z.string(), versionId: z.string(), warnings: actionWarningsSchema })
};
//# sourceMappingURL=email-agents.js.map